import { ReactNode, useState } from 'react';

import {
  GiftPartnerApplyDto,
  GiftPartnerContact,
} from '@shared/api/giftPartner/giftPartner.interface';
import { Alert, Button, Checkbox, Form, Input, Modal, Select } from 'antd';

import { brandUnavailable, hookUrlError, isIp, OverviewBrand } from './giftPartner.utils';
import './giftPartner.css';

export const PartnerSection = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => (
  <section className="min-w-0 space-y-4 rounded-lg border border-gray-200 bg-white p-4 sm:p-5">
    <h2 className="text-base font-semibold text-gray-900">{title}</h2>
    {children}
  </section>
);

export const PartnerError = ({ error }: { error?: string }) =>
  error ? <Alert type="error" showIcon message={error} /> : null;

export const BrandChoices = ({
  brands,
  value = [],
  onChange = () => undefined,
  partnerId,
}: {
  brands: OverviewBrand[];
  value?: string[];
  onChange?: (v: string[]) => void;
  partnerId?: string;
}) => (
  <div className="space-y-2">
    <p className="text-sm text-gray-600">
      같은 서버로 받을 브랜드를 모두 선택하세요. 연동키 하나로 선택한 브랜드의 발주를 전부
      받습니다. 브랜드마다 받는 서버가 다르면 따로 신청하세요.
    </p>
    {brands.map((b) => {
      const reason = brandUnavailable(b, partnerId);
      return (
        <div key={b._id} className="rounded border border-gray-200 px-3 py-2">
          <Checkbox
            disabled={!!reason}
            checked={value.includes(b._id)}
            onChange={(e) =>
              onChange(
                e.target.checked ? [...value, b._id] : value.filter((id) => id !== b._id),
              )
            }
          >
            {b.name}
          </Checkbox>
          {reason && <span className="ml-2 text-xs text-gray-500">{reason}</span>}
        </div>
      );
    })}
  </div>
);

export const ConnectionFields = ({ includeUrl = true }: { includeUrl?: boolean }) => (
  <>
    <Form.Item
      name="allowedIps"
      label="API를 호출할 서버 IP"
      required
      extra="API를 호출할 서버의 고정 공인 IP를 입력하세요. 사무실 PC나 개발자 노트북 IP가 아닙니다. 등록되지 않은 IP에서는 호출이 거부됩니다. Enter로 추가하세요."
      rules={[
        {
          validator: async (_, value: string[] = []) => {
            if (!value.length) throw new Error('IP를 1개 이상 입력하세요');
            const invalid = value.find((ip) => !isIp(ip));
            if (invalid)
              throw new Error(
                `올바른 IP가 아닙니다: ${invalid} (CIDR·도메인·와일드카드 불가)`,
              );
          },
        },
      ]}
    >
      <Select
        aria-label="API를 호출할 서버 IP"
        mode="tags"
        tokenSeparators={[',', ' ', ';']}
        placeholder="예: 203.0.113.10"
        open={false}
      />
    </Form.Item>
    {includeUrl && (
      <Form.Item
        name="webhookUrl"
        label="알림을 받을 주소 (콜백 URL · 선택)"
        required={false}
        normalize={(value: string) => value.trim()}
        extra="발주·배송지 입력·취소 알림을 받는 https 주소입니다. 없어도 목록 조회 API로 연동할 수 있습니다."
        rules={[
          {
            validator: async (_, value = '') => {
              const error = hookUrlError(value);
              if (error) throw new Error(error);
            },
          },
        ]}
      >
        <Input placeholder="https://partner.example.com/webhook" />
      </Form.Item>
    )}
  </>
);

export const ContactFields = () => (
  <>
    <p className="mb-3 text-sm text-gray-600">
      승인 전 관리자가 이 연락처로 본인 확인 전화를 합니다. 개발 담당자 연락처를 입력해
      주세요.
    </p>
    <Form.Item name={['contact', 'name']} label="개발 담당자">
      <Input />
    </Form.Item>
    <Form.Item name={['contact', 'phoneNumber']} label="전화번호">
      <Input type="tel" />
    </Form.Item>
    <Form.Item
      name={['contact', 'email']}
      label="이메일"
      rules={[{ type: 'email', message: '이메일 형식을 확인해 주세요' }]}
    >
      <Input type="email" />
    </Form.Item>
  </>
);

export const ApplicationForm = ({
  brands,
  initialIds,
  initial,
  admin = false,
  busy,
  error,
  onSubmit,
  onCancel,
}: {
  brands: OverviewBrand[];
  initialIds?: string[];
  initial?: Partial<GiftPartnerApplyDto>;
  admin?: boolean;
  busy: boolean;
  error: string;
  onSubmit: (dto: GiftPartnerApplyDto & { memo?: string }) => void;
  onCancel?: () => void;
}) => (
  <Form
    layout="vertical"
    scrollToFirstError
    initialValues={{
      allowedIps: [],
      ...initial,
      giftItemBrands:
        initialIds ?? brands.filter((b) => !brandUnavailable(b)).map((b) => b._id),
    }}
    onFinish={onSubmit}
    disabled={busy}
  >
    {!admin && (
      <h3 className="mb-4 text-sm font-semibold">1. 어떤 브랜드의 발주를 받을까요?</h3>
    )}
    <Form.Item
      name="giftItemBrands"
      label="브랜드"
      rules={[
        {
          required: true,
          type: 'array',
          min: 1,
          message: '브랜드를 1개 이상 선택하세요',
        },
        {
          validator: async (_, ids: string[] = []) => {
            if (
              ids.some((id) => !brands.some((b) => b._id === id && !brandUnavailable(b)))
            )
              throw new Error(
                '이미 신청 또는 연동된 브랜드가 포함되어 있습니다. 선택을 확인해 주세요.',
              );
          },
        },
      ]}
    >
      {brands.length === 1 && brands[0] && !brandUnavailable(brands[0]) ? (
        <SingleBrand name={brands[0].name} />
      ) : (
        <BrandChoices brands={brands} />
      )}
    </Form.Item>
    <Form.Item
      name="name"
      label={admin ? '연동 이름' : '연동 이름 (권장)'}
      rules={
        admin
          ? [{ required: true, whitespace: true, message: '연동 이름을 입력하세요' }]
          : []
      }
      extra="서버를 구분할 수 있도록 입력해 주세요. 예: 본사 서버, 물류센터"
    >
      <Input />
    </Form.Item>
    {!admin && (
      <h3 className="gift-partner-form-heading">2. 발주를 받을 서버를 알려주세요</h3>
    )}
    <ConnectionFields includeUrl={!admin} />
    {!admin && (
      <h3 className="gift-partner-form-heading">
        3. 신청 내용을 확인할 담당자를 알려주세요
      </h3>
    )}
    <ContactFields />
    {admin && (
      <Form.Item name="memo" label="관리자 메모">
        <Input.TextArea rows={3} />
      </Form.Item>
    )}
    <PartnerError error={error} />
    {!admin && (
      <p className="mt-4 rounded bg-indigo-50 p-3 text-sm text-indigo-900">
        아래 버튼은 연동 신청서를 제출합니다. 제출 후 다음 화면에서 API 키를 발급받을 수
        있으며, 실제 사용은 관리자 승인 후 가능합니다.
      </p>
    )}
    <div className="mt-4 flex flex-wrap gap-2">
      <Button
        htmlType="submit"
        type="primary"
        className="gift-partner-primary"
        loading={busy}
      >
        {admin ? '등록 및 키 발급' : '신청하고 키 발급으로'}
      </Button>
      {onCancel && <Button onClick={onCancel}>취소</Button>}
    </div>
  </Form>
);

// Retain the explicit brand array in Form even when the single-brand checkbox is omitted.
const SingleBrand = ({
  name,
}: {
  name: string;
  value?: string[];
  onChange?: (v: string[]) => void;
}) => <p className="font-medium">{name}</p>;

export const ContactForm = ({
  contact,
  busy,
  onSubmit,
}: {
  contact?: GiftPartnerContact;
  busy: boolean;
  onSubmit: (v: GiftPartnerContact) => void;
}) => (
  <Form
    layout="vertical"
    initialValues={{ contact }}
    onFinish={(v) => onSubmit(v.contact ?? {})}
    disabled={busy}
  >
    <ContactFields />
    <Button
      htmlType="submit"
      type="primary"
      className="gift-partner-primary"
      loading={busy}
    >
      담당자 저장
    </Button>
  </Form>
);

export interface PartnerSecret {
  title: string;
  value: string;
  partnerName: string;
  usageNotice?: string;
}
export const SecretModal = ({
  secret,
  onClose,
}: {
  secret: PartnerSecret;
  onClose: () => void;
}) => {
  const [saved, setSaved] = useState(false);
  const [copyMessage, setCopyMessage] = useState('');
  return (
    <Modal
      okButtonProps={{ className: 'gift-partner-primary' }}
      open
      title={`${secret.partnerName} · ${secret.title}`}
      closable={false}
      maskClosable={false}
      keyboard={false}
      footer={
        <Button
          type="primary"
          className="gift-partner-primary"
          disabled={!saved}
          onClick={onClose}
        >
          닫기
        </Button>
      }
    >
      <div className="space-y-4">
        {secret.usageNotice && <Alert type="info" message={secret.usageNotice} />}
        <Alert
          type="warning"
          showIcon
          message="이 창을 닫으면 다시 볼 수 없습니다. 안전한 곳에 저장하세요. 분실하면 폐기 후 재발급해야 합니다."
        />
        <pre className="whitespace-pre-wrap break-all rounded bg-gray-100 p-3 text-sm">
          {secret.value}
        </pre>
        <Button
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(secret.value);
              setCopyMessage('복사했습니다');
            } catch {
              setCopyMessage('복사하지 못했습니다. 위 값을 직접 선택해 복사해 주세요.');
            }
          }}
        >
          복사
        </Button>
        <p role="status">{copyMessage}</p>
        <Checkbox checked={saved} onChange={(e) => setSaved(e.target.checked)}>
          저장했습니다
        </Checkbox>
      </div>
    </Modal>
  );
};
